import { ButtonLoading } from "@/components/button-loading";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/hooks/useTranslation";
import { toHumanReadableAmount } from "@/lib/string";
import { TeamBillingPriceDTO, UserLicenseChangePreviewDTO } from "@shaarp/common";
import { CreditCardIcon } from "lucide-react";
import { useState } from "react";

interface DialogUserLicenseUpgradeConfirmProps {
  preview: UserLicenseChangePreviewDTO;
  targetPrice: TeamBillingPriceDTO;
  isLoading: boolean;
  onConfirm: () => void;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Step 2 of the Anthropic-inspired user license change flow.
 * Shows seat breakdown, proration, payment method, and requires checkbox confirmation.
 */
export const DialogUserLicenseUpgradeConfirm = ({
  preview,
  targetPrice,
  isLoading,
  onConfirm,
  onBack,
  onClose,
}: DialogUserLicenseUpgradeConfirmProps) => {
  const { t, i18n } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);

  const formatAmount = (amount: number) => {
    if (!preview.currency) {
      return toHumanReadableAmount(amount);
    }
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: preview.currency.toUpperCase(),
      }).format(amount);
    } catch {
      return toHumanReadableAmount(amount);
    }
  };

  const frequencyLabel =
    targetPrice.interval === "month"
      ? t("team.licenseManagement.monthly").toLowerCase()
      : t("team.licenseManagement.annual").toLowerCase();

  const hasCharge = preview.immediateChargeInclTax > 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[480px] w-full">
        <DialogHeader>
          <DialogTitle>{t("team.userLicenseChange.confirmTitle")}</DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="space-y-3 text-sm">
          {/* Seat totals */}
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("team.userLicenseChange.totalSeats")}
            </span>
            <span className="font-medium">
              {preview.totalSeatsBefore} → {preview.totalSeatsAfter}
            </span>
          </div>

          {preview.newRecurringExclTax !== null && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("team.userLicenseChange.newRecurring", {
                  frequency: frequencyLabel,
                })}
              </span>
              <span className="font-medium">
                {formatAmount(preview.newRecurringExclTax)}{" "}
                <span className="text-muted-foreground text-xs">
                  {t("team.userLicenseChange.taxesNote")}
                </span>
              </span>
            </div>
          )}

          {/* Proration lines */}
          {preview.prorationLines.length > 0 && (
            <>
              <Separator />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {t("team.userLicenseChange.dueToday")}
              </p>
              {preview.prorationLines.map((line, idx) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">
                    {line.label}
                    {line.quantityDelta !== 0 && (
                      <span className="ml-1">
                        ({line.quantityDelta > 0 ? "+" : ""}
                        {line.quantityDelta})
                      </span>
                    )}
                  </span>
                </div>
              ))}

              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t("team.userLicenseChange.subtotal")}</span>
                <span>{formatAmount(preview.immediateChargeExclTax)}</span>
              </div>

              {preview.immediateTax > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t("team.userLicenseChange.tax")}</span>
                  <span>{formatAmount(preview.immediateTax)}</span>
                </div>
              )}

              <div className="flex justify-between font-semibold border-t pt-2">
                <span>{t("team.userLicenseChange.totalDue")}</span>
                <span>{formatAmount(preview.immediateChargeInclTax)}</span>
              </div>
            </>
          )}

          {/* Payment method */}
          <Separator />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CreditCardIcon className="w-4 h-4" />
            <span>{t("team.userLicenseChange.paymentMethod")}</span>
            {preview.paymentMethodBrand && preview.paymentMethodLast4 ? (
              <span className="font-medium text-foreground">
                {preview.paymentMethodBrand.charAt(0).toUpperCase() +
                  preview.paymentMethodBrand.slice(1)}{" "}
                ····{preview.paymentMethodLast4}
              </span>
            ) : (
              <span className="text-destructive">
                {t("team.userLicenseChange.noPaymentMethod")}
              </span>
            )}
          </div>

          {/* Disclaimer + checkbox */}
          {hasCharge && (
            <>
              <Separator />
              <p className="text-xs text-muted-foreground">
                {t("team.userLicenseChange.disclaimer")}
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={confirmed}
                  onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
                  className="mt-0.5"
                />
                <span className="text-xs">
                  {t("team.userLicenseChange.confirmCheckbox")}
                </span>
              </label>
            </>
          )}
        </div>

        <Separator />

        <DialogFooter>
          <Button variant="ghost" onClick={onBack} disabled={isLoading}>
            {t("team.userLicenseChange.back")}
          </Button>
          <ButtonLoading
            isLoading={isLoading}
            disabled={
              isLoading ||
              (hasCharge && !confirmed) ||
              (!preview.paymentMethodLast4 && hasCharge)
            }
            onClick={onConfirm}
          >
            {t("team.userLicenseChange.confirmAndPurchase")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
